import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { DillDispute } from '@dill/dill-shared';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { DisputesInitialState } from '../globalSlices/disputesSlice';
import {
  getSupplierDisputes,
  getSupplierOpenDisputesCount,
  updateDispute,
  createDispute,
} from '../services/disputesService';

export const createDisputeExtraReducer = (
  builder: ActionReducerMapBuilder<DisputesInitialState>
) => {
  const reducerId = 'createDispute';
  builder.addCase(createDispute.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createDispute.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        const newDispute = action.payload.data as DillDispute;
        state.disputes = [newDispute, ...state.disputes];
        if (!newDispute.isResolved) {
          state.supplierOpenDisputesCount += 1;
        }
      }
    }
  );
  builder.addCase(createDispute.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierDisputesExtraReducer = (
  builder: ActionReducerMapBuilder<DisputesInitialState>
) => {
  const reducerId = 'getSupplierDisputes';
  builder.addCase(getSupplierDisputes.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierDisputes.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.disputes = action.payload?.data;
        state.supplierOpenDisputesCount = Number(
          (action.payload?.data || []).filter((dispute: any) => !dispute?.isResolved).length
        );
      }
    }
  );
  builder.addCase(getSupplierDisputes.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierOpenDisputesCountExtraReducer = (
  builder: ActionReducerMapBuilder<DisputesInitialState>
) => {
  const reducerId = 'getSupplierOpenDisputesCount';
  builder.addCase(getSupplierOpenDisputesCount.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.supplierOpenDisputesCount = 0;
  });
  builder.addCase(
    getSupplierOpenDisputesCount.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data?.count) {
        state.supplierOpenDisputesCount = Number(action.payload?.data?.count);
      }
    }
  );
  builder.addCase(getSupplierOpenDisputesCount.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateDisputeExtraReducer = (
  builder: ActionReducerMapBuilder<DisputesInitialState>
) => {
  const reducerId = 'updateDispute';
  builder.addCase(updateDispute.pending, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(updateDispute.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    // Update the dispute in the local state
    const { disputeId, isResolved } = action.meta.arg;
    const disputeIndex = state.disputes.findIndex((d) => d.id === disputeId);
    if (disputeIndex !== -1) {
      state.disputes[disputeIndex] = {
        ...state.disputes[disputeIndex],
        isResolved,
      };
    }
  });
  builder.addCase(updateDispute.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};
